import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

import dayjs from '~libs/dayjs';

import CascadingSelectors from '~components/form-fields/inputs/CascadingSelectors';

import { getAll as getPricePlanRevisions } from '~support-at-home/actions/pricePlanRevisions';
import { clear as clearPricePlans } from '~support-at-home/reducers/pricePlanRevisions';
import { selectPricePlanRevisionOptions } from '~support-at-home/selectors/pricePlanRevisions';
import { selectPricePlans, selectPricePlansHasLoaded } from '~support-at-home/selectors/pricePlans';

import { PricePlan } from '~support-at-home/models';

interface ComponentProps {
  value: { price_plan_id?: string; price_plan_revision_id?: string; start_date?: string; end_date?: string };
  // Only nullable string values flow through here (a plan/revision id, or null to clear). `name` stays
  // `string` because this is forwarded straight to CascadingSelectors, whose InputProps.onChange contract
  // is keyed by an open `string` — narrowing it to the two field literals would break that assignment.
  onChange?: (name: string, value: string | null) => void;
}

// A plan covers the chosen dates when it does not start after the new start and does not end before the new end.
// Mirrors the API's #assertTargetRevisionWindow. Empty dates => no constraint (all plans valid).
const isPlanWithinDates = (plan: PricePlan, startDate?: string, endDate?: string) => {
  const startsBeforePlan = startDate && plan.start_date && dayjs(startDate).isBefore(dayjs(plan.start_date), 'day');
  const endsAfterPlan = endDate && plan.end_date && dayjs(endDate).isAfter(dayjs(plan.end_date), 'day');
  return !startsBeforePlan && !endsAfterPlan;
};

const PricePlanPicker: React.FC<ComponentProps> = ({ value, onChange }) => {
  const { price_plan_id, start_date, end_date } = value;
  const allPlans = useSelector((state: RootState) => selectPricePlans(state));
  const plansHaveLoaded = useSelector(selectPricePlansHasLoaded);

  const validPlans = useMemo(
    () => allPlans.filter((plan) => isPlanWithinDates(plan, start_date, end_date)),
    [allPlans, start_date, end_date],
  );

  // Clear a selected plan that is no longer valid for the chosen dates (e.g. after the user edits the dates).
  // Wait until the plans have actually loaded: before the fetch settles `validPlans` is empty, which would
  // otherwise wipe a seeded selection (e.g. re-entering this wizard step) before the plan list has arrived.
  useEffect(() => {
    if (!onChange || !price_plan_id || !plansHaveLoaded) return;
    if (!validPlans.some((plan) => plan.id === price_plan_id)) {
      onChange('price_plan_id', null);
      onChange('price_plan_revision_id', null);
    }
  }, [price_plan_id, validPlans, plansHaveLoaded, onChange]);

  // Reuse the memoized validPlans so CascadingSelectors' useSelector gets a stable reference
  // (a fresh filtered array each call would defeat its reference-equality check) and the date-window
  // filter lives in exactly one place.
  const selectValidPlans = () => validPlans;

  return (
    <CascadingSelectors
      type="custom"
      id="price_plan"
      name="price_plan"
      value={value}
      onChange={onChange}
      cascadingSelectors={[
        {
          position: 1,
          id: 'price_plan_id',
          name: 'price_plan_id',
          selectorOptions: selectValidPlans,
          fKey: 'plan_id',
        },
        {
          position: 2,
          id: 'price_plan_revision_id',
          name: 'price_plan_revision_id',
          selectorActions: getPricePlanRevisions,
          selectorOptions: selectPricePlanRevisionOptions,
          reducerFunctions: { clear: clearPricePlans },
          label: 'Rev. ',
          selectTop: true,
        },
      ]}
    />
  );
};

export default PricePlanPicker;
